import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { PromoConfig } from '@/types/promo';
import type { ShopifyProduct } from '@/types/shopify';
import { fetchRuntimeActivePromos } from '@/services/promo/promoApi';

const PROMO_REFRESH_INTERVAL_MS = 60_000;

interface PromoContextValue {
  /** Toutes les promos actives. */
  promos: PromoConfig[];
  /** Première promo active (compatibilité ascendante avec les composants existants). */
  promo: PromoConfig | null;
  /** Vrai si au moins une promo est active. */
  isPromoActive: boolean;
  loading: boolean;
  refreshPromo: () => Promise<void>;
  /**
   * Retourne la promo applicable à ce produit (en fonction de ses collections),
   * ou null si aucune promo ne le concerne.
   */
  getProductPromo: (product: ShopifyProduct) => PromoConfig | null;
}

const PromoContext = createContext<PromoContextValue | undefined>(undefined);

/**
 * Vérifie si un produit est éligible à une promo donnée.
 * Une promo sans collections ciblées s'applique à toute la boutique.
 */
function isProductEligible(product: ShopifyProduct, promo: PromoConfig): boolean {
  const { collections: promoCollections } = promo;
  if (!promoCollections || promoCollections.length === 0) return true;
  return (
    product.collections?.some((pc) =>
      promoCollections.some((c) => c.handle === pc.handle),
    ) ?? false
  );
}

export const PromoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [promos, setPromos] = useState<PromoConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshPromo = useCallback(async () => {
    try {
      const nextPromos = await fetchRuntimeActivePromos();
      setPromos(nextPromos);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      const nextPromos = await fetchRuntimeActivePromos();
      if (isMounted) {
        setPromos(nextPromos);
        setLoading(false);
      }
    })();

    const intervalId = window.setInterval(() => {
      void refreshPromo();
    }, PROMO_REFRESH_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [refreshPromo]);

  const getProductPromo = useCallback(
    (product: ShopifyProduct): PromoConfig | null => {
      return promos.find((promo) => isProductEligible(product, promo)) ?? null;
    },
    [promos],
  );

  const value = useMemo<PromoContextValue>(
    () => ({
      promos,
      promo: promos[0] ?? null,
      isPromoActive: promos.length > 0,
      loading,
      refreshPromo,
      getProductPromo,
    }),
    [promos, loading, refreshPromo, getProductPromo],
  );

  return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
};

export function usePromoContext(): PromoContextValue {
  const ctx = useContext(PromoContext);
  if (!ctx) {
    throw new Error('usePromoContext must be used within PromoProvider');
  }
  return ctx;
}
